import {computed} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import type {RouteLocationRaw} from 'vue-router'
import { useOrgStore } from '@/stores/org'
import {ROUTE_PARENT, ROUTE_ROOTS, FALLBACK_PARENT} from '@/router/hierarchy'

/**
 * Resolves the back button target from the declared page hierarchy rather than
 * from browser history, so a page always goes to the same parent — including on
 * a cold deep-link with no history to go back to.
 */
export function useBackTarget() {
  const route = useRoute()
  const router = useRouter()
  const orgStore = useOrgStore()

  // Structural and store-free, so `show-back` is stable on first paint.
  // vue-router inherits the target's required params from the current location,
  // so passing params here would only produce "discarded invalid param" warnings.
  const backTarget = computed<RouteLocationRaw | null>(() => {
    const name = typeof route.name === 'string' ? route.name : null
    if (!name || ROUTE_ROOTS.has(name)) return null

    const parent = ROUTE_PARENT[name]
    if (typeof parent === 'function') return parent(route)
    return {name: parent || FALLBACK_PARENT}
  })

  const targetName = (to: RouteLocationRaw) =>
    typeof to === 'object' && 'name' in to && typeof to.name === 'string' ? to.name : null

  const needsOrgId = (name: string) =>
    !!router.getRoutes().find(r => r.name === name)?.path.includes(':org_id')

  const goBack = async () => {
    const target = backTarget.value
    if (!target) return

    let to = target
    const name = targetName(target)
    if (name && !route.params.org_id && needsOrgId(name)) {
      const orgId = orgStore.selectedOrg || await orgStore.resolveSelectedOrg()
      if (!orgId) return
      to = {...(target as object), params: {org_id: orgId}} as RouteLocationRaw
    }

    try {
      await router.push(to)
    } catch (err) {
      console.error('[useBackTarget] navigation failed', err)
    }
  }

  return {backTarget, goBack}
}
